<script setup>
import { ref } from 'vue'
import { RESOURCE_TYPES, TOPICS } from '@/utils/resourceTags.js'
import { trackEvent } from '@/composables/useAnalytics.js'

const props = defineProps({
  activeTypes:  { type: Array, default: () => [] },
  activeTopics: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:types', 'update:topics', 'clear'])

const isOpen = ref(false)

function toggleType(value) {
  const isActive = props.activeTypes.includes(value)
  const next = isActive
    ? props.activeTypes.filter(v => v !== value)
    : [...props.activeTypes, value]
  trackEvent('filter_resources', {
    filter_type: 'type',
    filter_value: value,
    action: isActive ? 'remove' : 'add',
  })
  emit('update:types', next)
}

function toggleTopic(value) {
  const isActive = props.activeTopics.includes(value)
  const next = isActive
    ? props.activeTopics.filter(v => v !== value)
    : [...props.activeTopics, value]
  trackEvent('filter_resources', {
    filter_type: 'topics',
    filter_value: value,
    action: isActive ? 'remove' : 'add',
  })
  emit('update:topics', next)
}

function clearAll() {
  emit('clear')
}
</script>

<template>
  <aside class="filters">
    <div class="drawer">
      <button
        type="button"
        class="toggle"
        :aria-expanded="isOpen"
        aria-controls="resource-filters-panel"
        @click="isOpen = !isOpen"
      >
        Filters
      </button>

      <div id="resource-filters-panel" class="panel" v-show="isOpen">
        <button
          v-if="activeTypes.length || activeTopics.length"
          type="button"
          class="clear"
          @click="clearAll"
        >
          Clear all
        </button>

        <fieldset class="group">
          <legend class="legend">Resource type</legend>
          <div class="options">
            <label v-for="opt in RESOURCE_TYPES" :key="opt.value" class="option">
              <input
                type="checkbox"
                :checked="activeTypes.includes(opt.value)"
                @change="toggleType(opt.value)"
              />
              {{ opt.label }}
            </label>
          </div>
        </fieldset>

        <fieldset class="group">
          <legend class="legend">Topic</legend>
          <div class="options">
            <label v-for="opt in TOPICS" :key="opt.value" class="option">
              <input
                type="checkbox"
                :checked="activeTopics.includes(opt.value)"
                @change="toggleTopic(opt.value)"
              />
              {{ opt.label }}
            </label>
          </div>
        </fieldset>
      </div>
    </div>
  </aside>
</template>

<style lang="scss" scoped>
.filters {
  .drawer {
    border: 1px solid var(--color-border);
    border-radius: $radius;
    padding: $space-4;

    @include from-lg {
      border: none;
      padding: 0;
    }
  }

  .toggle {
    display: block;
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    cursor: pointer;
    font-family: $font-sans;
    font-weight: 600;
    font-size: $font-size-base;
    color: var(--color-ink);
    padding: 0;
    &:focus-visible { @include focus-ring; }

    @include from-lg { display: none; }
  }

  .panel {
    margin-top: $space-4;
    display: flex;
    flex-direction: column;
    gap: $space-5;

    @include from-lg {
      display: flex !important;
      margin-top: 0;
    }
  }

  .clear {
    align-self: flex-start;
    background: none;
    border: none;
    color: var(--color-primary);
    cursor: pointer;
    font-family: $font-sans;
    font-size: $font-size-sm;
    padding: 0;
    &:hover         { text-decoration: underline; }
    &:focus-visible { @include focus-ring; }
    &:active        { color: var(--color-primary-press); }
  }

  .group {
    border: none;
    padding: 0;
    margin: 0;

    .legend {
      font-size: $font-size-cap;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--color-text-muted);
      margin-bottom: $space-3;
      padding: 0;
    }

    .options {
      display: flex;
      flex-direction: column;
      gap: $space-3;
    }

    .option {
      display: flex;
      align-items: center;
      gap: $space-2;
      font-size: $font-size-sm;
      color: var(--color-text);
      cursor: pointer;

      input {
        accent-color: var(--color-primary);
        width: 18px;
        height: 18px;
        flex-shrink: 0;
        &:focus-visible { @include focus-ring; }
      }
    }
  }
}
</style>
